import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageTransition from '../components/PageTransition';
import Loader from '../components/Loader';
import useLoading from '../hooks/useLoading';
import useImagePreload from '../hooks/useImagePreload';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PaintBrushIcon,
    Squares2X2Icon,
    WrenchScrewdriverIcon,
    BuildingLibraryIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';

const Home = () => {
    const isLoading = useLoading(1500);
    const imagesLoaded = useImagePreload([
        'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6',
        'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0',
        'https://images.unsplash.com/photo-1537726235470-8504e3beef77',
        'https://images.unsplash.com/photo-1497366216548-37526070297c',
    ]);

    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const featuredProjects = [
        {
            id: 1,
            title: 'Modern Minimalist Villa',
            category: 'Residential',
            image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0',
        },
        {
            id: 2,
            title: 'Luxury Restaurant Design',
            category: 'Commercial',
            image: 'https://images.unsplash.com/photo-1537726235470-8504e3beef77',
        },
        {
            id: 3,
            title: 'Contemporary Office Space',
            category: 'Corporate',
            image: 'https://images.unsplash.com/photo-1497366216548-37526070297c',
        },
    ];

    const services = [
        {
            title: 'Interior Design',
            description: 'Complete interior design solutions for residential and commercial spaces.',
            icon: PaintBrushIcon,
        },
        {
            title: 'Space Planning',
            description: 'Optimal space utilization and layout planning for maximum efficiency.',
            icon: Squares2X2Icon,
        },
        {
            title: 'Custom Furniture',
            description: 'Bespoke furniture design and manufacturing services.',
            icon: WrenchScrewdriverIcon,
        },
        {
            title: 'Renovation',
            description: 'Full-scale renovation and remodeling projects.',
            icon: BuildingLibraryIcon,
        },
    ];

    if (isLoading || !imagesLoaded) {
        return <Loader />;
    }

    return (
        <PageTransition>
            <SEO
                title="Parth Interiors - Luxury Interior Design Services"
                description="Transform your space with Parth Interiors. Expert interior design services for residential, commercial, and corporate spaces. Creating timeless and innovative designs."
            />

            <div className="min-h-screen bg-dark">
                {/* Hero Section */}
                <section className="relative h-screen flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 z-0">
                        <motion.img
                            initial={{ scale: 1.1 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 1.5, ease: "easeOut" }}
                            src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6"
                            alt="Luxury Interior Design"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80"></div>
                    </div>

                    <motion.div
                        className="relative z-10 text-center text-light max-w-4xl mx-auto px-4"
                        {...fadeIn}
                    >
                        <h1 className="font-serif text-5xl md:text-7xl font-bold mb-6">
                            Transform Your Space
                        </h1>
                        <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto text-light/90">
                            Creating timeless and innovative designs for your dream space
                        </p>
                        <Link
                            to="/contact"
                            className="btn-primary inline-block text-lg group"
                        >
                            Get Started
                            <ArrowRightIcon className="w-5 h-5 inline-block ml-2 transform group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                    </motion.div>
                </section>

                {/* Featured Projects */}
                <section className="py-24 bg-dark/50">
                    <div className="container mx-auto px-4">
                        <motion.div
                            className="text-center mb-16"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="font-serif text-4xl font-bold text-light mb-4">Featured Projects</h2>
                            <p className="text-light/80 text-lg">Discover our latest interior design projects</p>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {featuredProjects.map((project, index) => (
                                <motion.div
                                    key={project.id}
                                    className="card group overflow-hidden bg-dark/30 hover:bg-dark/50 transition-all duration-300"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <div className="relative h-80 overflow-hidden">
                                        <motion.img
                                            src={project.image}
                                            alt={project.title}
                                            className="w-full h-full object-cover"
                                            whileHover={{ scale: 1.1 }}
                                            transition={{ duration: 0.7 }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                        <motion.div
                                            className="absolute bottom-0 left-0 right-0 p-6"
                                            initial={{ y: 100 }}
                                            whileHover={{ y: 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="font-serif text-2xl font-bold text-light mb-2">{project.title}</h3>
                                                    <p className="text-light/80">{project.category}</p>
                                                </div>
                                                <ArrowRightIcon className="w-6 h-6 text-light transform group-hover:translate-x-1 transition-transform duration-300" />
                                            </div>
                                        </motion.div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <div className="text-center mt-16">
                            <Link to="/projects" className="btn-secondary group">
                                View All Projects
                                <ArrowRightIcon className="w-5 h-5 inline-block ml-2 transform group-hover:translate-x-1 transition-transform duration-300" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Services Section */}
                <section className="py-24 bg-primary text-light">
                    <div className="container mx-auto px-4">
                        <motion.div
                            className="text-center mb-16"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="font-serif text-4xl font-bold mb-4">Our Services</h2>
                            <p className="text-light/80 text-lg">Comprehensive interior design solutions for every space</p>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {services.map((service, index) => (
                                <motion.div
                                    key={index}
                                    className="card bg-white/10 backdrop-blur-sm group hover:bg-white/20 transition-all duration-300"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    whileHover={{ y: -5 }}
                                >
                                    <motion.div
                                        whileHover={{ scale: 1.1 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <service.icon className="w-12 h-12 text-accent mb-4" />
                                    </motion.div>
                                    <h3 className="font-serif text-xl font-bold mb-3">{service.title}</h3>
                                    <p className="text-light/80">{service.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Call to Action */}
                <section className="py-24 bg-accent text-dark text-center">
                    <div className="container mx-auto px-4">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="max-w-3xl mx-auto"
                        >
                            <h2 className="font-serif text-4xl font-bold mb-6">Ready to Transform Your Space?</h2>
                            <p className="text-xl mb-8 text-dark/80">Let's create something extraordinary together</p>
                            <Link to="/contact" className="btn-primary bg-dark text-light hover:bg-dark/90 group">
                                Get in Touch
                                <ArrowRightIcon className="w-5 h-5 inline-block ml-2 transform group-hover:translate-x-1 transition-transform duration-300" />
                            </Link>
                        </motion.div>
                    </div>
                </section>
            </div>
        </PageTransition>
    );
};

export default Home; 